// The name a downloaded payslip PDF is saved under — one definition for the
// employee's own download (payslip-content) and the HR-side one
// (run-statement-list), so the two cannot drift again.
//
//   employee side:  "bordro-2026-06.pdf"
//   HR side:        "bordro-2026-06-E-1008.pdf"
//
// The name used to end in the first eight characters of the statement id. That
// says nothing to the person saving it, and because ids are time-ordered it
// repeats across payslips created in the same minute; an HR user saving a
// hundred of them got a folder of indistinguishable files. A period that cannot
// be resolved falls back to a shorter name, never to an id fragment.

/** Anything that would let a saved file's name escape or confuse a file system. */
const UNSAFE = /[^A-Za-z0-9._-]+/g;

export interface PayslipFileNameParts {
  /** The localized word for "payslip" ("bordro"). */
  readonly prefix: string;
  /** The run's first day ("2026-06-01"); null when the period is not known. */
  readonly periodStart: string | null;
  /**
   * Who the payslip belongs to. Only the HR side passes it: an employee's own
   * download is already theirs. The sicil is preferred (short, unique in the
   * tenant); a name stands in when there is none.
   */
  readonly employeeNumber?: string | null;
  readonly employeeName?: string | null;
}

function slug(text: string): string {
  return (
    text
      .normalize("NFD")
      // U+0131 has no decomposition, so "ı" would otherwise be stripped as unsafe.
      .replace(/ı/g, "i")
      .replace(/\p{M}+/gu, "")
      .replace(UNSAFE, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase()
  );
}

export function payslipFileName({
  prefix,
  periodStart,
  employeeNumber,
  employeeName,
}: PayslipFileNameParts): string {
  const month = periodStart === null ? undefined : /^\d{4}-\d{2}/.exec(periodStart)?.[0];
  const person =
    employeeNumber != null && employeeNumber.trim() !== ""
      ? employeeNumber.trim().replace(UNSAFE, "-")
      : employeeName != null
        ? slug(employeeName)
        : "";
  return [prefix, month, person === "" ? undefined : person].filter(Boolean).join("-") + ".pdf";
}
